// /managers/ConvoManager/ConvoHelpers.jsx
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  writeBatch as createWriteBatch,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  limitToLast,
  startAfter,
  endBefore,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { CryptoUtils } from '@/utils/cryptoUtils';
import CryptoWorker from './crypto.worker.js?worker';

// --- Worker Setup for Off-Thread Decryption ---
let decryptionWorker = null;
let messageIdCounter = 0;
const pendingDecryptionRequests = new Map();

if (typeof window !== 'undefined' && window.Worker) {
  try {
    decryptionWorker = new CryptoWorker();

    decryptionWorker.onmessage = (event) => {
      const { id, result } = event.data;
      if (pendingDecryptionRequests.has(id)) {
        const { resolve } = pendingDecryptionRequests.get(id);
        resolve(result);
        pendingDecryptionRequests.delete(id);
      }
    };

    decryptionWorker.onerror = (error) => {
      for (const [id, { reject }] of pendingDecryptionRequests.entries()) {
        reject(new Error('Decryption worker encountered a fatal error.'));
        pendingDecryptionRequests.delete(id);
      }
    };
  } catch (e) {
    decryptionWorker = null;
  }
}

export const generateId = (prefix = '') => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `${prefix}_${timestamp}_${random}`;
};

const decryptOnMainThread = async (data, userId) => {
  if (!data) return '';
  try {
    const decrypted = await CryptoUtils.decrypt(data, userId);
    return decrypted;
  } catch (error) {
    if (error.message.includes('does not appear to be encrypted')) return data;
    return data;
  }
};

export const safeDecrypt = (data, userId) => {
  if (!data) return Promise.resolve('');

  const isPotentiallyEncrypted = typeof data === 'string' && data.startsWith('U2FsdGVkX');

  if (decryptionWorker && isPotentiallyEncrypted) {
    const id = messageIdCounter++;
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        pendingDecryptionRequests.delete(id);
        resolve(decryptOnMainThread(data, userId));
      }, 5000);

      pendingDecryptionRequests.set(id, {
        resolve: (result) => { clearTimeout(timeoutId); resolve(result); },
        reject: (error) => { clearTimeout(timeoutId); reject(error); }
      });
      
      decryptionWorker.postMessage({ id, data, userId });
    });
  }
  return decryptOnMainThread(data, userId);
};

export const safeDecryptAndParse = async (encryptedString, userId) => {
    if (!encryptedString) return [];
    try {
        const decryptedString = await safeDecrypt(encryptedString, userId);
        if (typeof decryptedString === 'string' && decryptedString.trim().startsWith('[')) {
            return JSON.parse(decryptedString);
        }
        if (Array.isArray(decryptedString)) {
            return decryptedString; 
        }
        const blockId = generateId('text_block');
        return [{ type: 'BLOCK_CREATE', payload: { id: blockId, type: 'text' } }, { type: 'BLOCK_DELTA', payload: { id: blockId, contentChunk: String(decryptedString) } }];
    } catch (e) {
        return [];
    }
};

export const fetchConversationMeta = async (firestore, userId, conversationId) => {
  try {
    const convoRef = doc(firestore, 'users', userId, 'conversations', conversationId);
    const convoDoc = await getDoc(convoRef);
    if (!convoDoc.exists()) return null;
    return { ...convoDoc.data(), id: convoDoc.id, persisted: true };
  } catch (error) {
    throw error;
  }
};
export const fetchConversation = fetchConversationMeta;

export const fetchRecentConversations = async (firestore, userId, maxConversations = 30) => {
  try {
    const q = query(collection(firestore, 'users', userId, 'conversations'), orderBy('lastUpdated', 'desc'), limit(maxConversations));
    const querySnapshot = await getDocs(q);
    const conversations = {};
    querySnapshot.docs.forEach(docSnap => { conversations[docSnap.id] = { ...docSnap.data(), id: docSnap.id, persisted: true }; });
    return conversations;
  } catch (error) { throw error; }
};

export const fetchOlderConversations = async (firestore, userId, lastUpdated, maxConversations = 30) => {
  try {
    const q = query(collection(firestore, 'users', userId, 'conversations'), orderBy('lastUpdated', 'desc'), where('lastUpdated', '<', lastUpdated), limit(maxConversations));
    const querySnapshot = await getDocs(q);
    const conversations = {};
    querySnapshot.docs.forEach(docSnap => { conversations[docSnap.id] = { ...docSnap.data(), id: docSnap.id, persisted: true }; });
    return conversations;
  } catch (error) { throw error; }
};

export const subscribeToConversationMeta = (firestore, userId, conversationId, callback) => {
  const convoRef = doc(firestore, 'users', userId, 'conversations', conversationId);
  return onSnapshot(convoRef, (docSnapshot) => {
    if (!docSnapshot.exists()) { callback(null); return; }
    callback({ ...docSnapshot.data(), id: docSnapshot.id, persisted: true });
  }, error => { /* Silent error handling */ });
};
export const subscribeToConversation = subscribeToConversationMeta;

export const fetchMessagesLazy = async (firestore, userId, conversationId, options = {}) => {
  try {
    const convoRef = doc(firestore, 'users', userId, 'conversations', conversationId);
    const constraints = [orderBy('timestamp', 'desc')]; 
    if (options.limit) constraints.push(limit(options.limit));
    if (options.startAfter) constraints.push(startAfter(options.startAfter));
    if (options.endBefore) constraints.push(endBefore(options.endBefore));
    
    const messagesQuery = query(collection(convoRef, 'messages'), ...constraints);
    const snapshot = await getDocs(messagesQuery);

    const messagesData = snapshot.docs.map(doc => {
      const data = doc.data();
      
      let timestamp;
      if (data.timestamp?.toDate) {
        timestamp = data.timestamp.toDate();
      } else if (data.timestamp instanceof Date) {
        timestamp = data.timestamp;
      } else if (typeof data.timestamp === 'number' || typeof data.timestamp === 'string') {
        timestamp = new Date(data.timestamp);
      } else {
        timestamp = new Date();
      }
      
      if (isNaN(timestamp.getTime())) {
        timestamp = new Date();
      }
      
      return {
        ...data,
        id: doc.id,
        timestamp,
      };
    });
    
    if (options.reverse) messagesData.reverse();
    
    return { messages: messagesData, snapshot };
  } catch (error) { 
    throw error; 
  }
};

export const fetchMessageContent = async (firestore, userId, conversationId, messageId) => {
  try {
    const messageRef = doc(firestore, 'users', userId, 'conversations', conversationId, 'messages', messageId);
    const messageDoc = await getDoc(messageRef);
    if (!messageDoc.exists()) return null;
    const data = messageDoc.data();
    
    const [rawContent, contentForUI, memoriesContext] = await Promise.all([
        safeDecrypt(data.rawContent || '', userId),
        safeDecryptAndParse(data.contentForUI || '[]', userId),
        data.retrievedMemoriesContext ? safeDecrypt(data.retrievedMemoriesContext, userId) : Promise.resolve('')
    ]);

    return { 
        ...data, 
        id: messageDoc.id, 
        rawContent, 
        streamEvents: contentForUI, 
        retrievedMemoriesContext: memoriesContext, 
        timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp || 0), 
        persisted: true 
    };
  } catch (error) { return null; }
};

export const subscribeToMessageUpdates = (firestore, userId, conversationId, callback) => {
  const convoRef = doc(firestore, 'users', userId, 'conversations', conversationId);
  const messagesQuery = query(collection(convoRef, 'messages'), orderBy('timestamp', 'desc'), limit(3));
  
  return onSnapshot(messagesQuery, (snapshot) => {
    snapshot.docChanges().forEach(change => {
      if (change.type === 'added' || change.type === 'modified') {
        const data = change.doc.data();
        
        let timestamp;
        if (data.timestamp?.toDate) {
          timestamp = data.timestamp.toDate();
        } else if (data.timestamp instanceof Date) {
          timestamp = data.timestamp;
        } else if (typeof data.timestamp === 'number' || typeof data.timestamp === 'string') {
          timestamp = new Date(data.timestamp);
        } else {
          timestamp = new Date();
        }
        
        if (isNaN(timestamp.getTime())) {
          timestamp = new Date();
        }
        
        const messageData = { 
            ...data, 
            id: change.doc.id, 
            timestamp
        };
        callback({ type: change.type, messageId: change.doc.id, data: messageData });
      }
    });
  }, error => { /* Silent error handling */ });
};

export const fetchFilesForMessages = async (firestore, userId, conversationId, messageIds) => {
  if (!messageIds || messageIds.length === 0) return {};
  const filesRef = collection(firestore, 'users', userId, 'conversations', conversationId, 'files');
  
  const attachmentsByMessageId = {};
  messageIds.forEach(id => {
    attachmentsByMessageId[id] = { files: [], images: [] };
  });

  const chunks = [];
  for (let i = 0; i < messageIds.length; i += 30) {
    chunks.push(messageIds.slice(i, i + 30));
  }
  
  for (const idChunk of chunks) {
    if (idChunk.length === 0) continue;
    const q = query(filesRef, where('messageId', 'in', idChunk));
    const snapshot = await getDocs(q);
    
    await Promise.all(snapshot.docs.map(async (docSnap) => {
      const data = docSnap.data();
      const messageId = data.messageId;
      
      const decryptedName = data.name ? await safeDecrypt(data.name, userId) : '';
      const storageUrl = data.storagePath ? await safeDecrypt(data.storagePath, userId) : '';
      
      const attachment = {
        ...data,
        id: docSnap.id,
        name: decryptedName,
        url: storageUrl, 
        storageUrl: storageUrl,
        persisted: true,
      };

      if (data.content && !data.type?.startsWith('image')) {
          attachment.content = await safeDecrypt(data.content, userId);
      }
      
      if (!attachmentsByMessageId[messageId]) {
          attachmentsByMessageId[messageId] = { files: [], images: [] };
      }

      if (data.type?.startsWith('image/')) {
        attachmentsByMessageId[messageId].images.push(attachment);
      } else {
        attachmentsByMessageId[messageId].files.push(attachment);
      }
    }));
  }
  
  return attachmentsByMessageId;
};

export const persistConversation = async (firestore, userId, conversationId, data) => {
  try {
    const convoRef = doc(firestore, 'users', userId, 'conversations', conversationId);
    await setDoc(convoRef, { ...data, persisted: true, lastUpdated: serverTimestamp() }, { merge: true });
    return true;
  } catch (error) { throw error; }
};

export const addMessageBatch = async (firestore, userId, conversationId, messageData, attachments = {}, existingBatch = null) => {
  const batch = existingBatch || createWriteBatch(firestore);
  const convoRef = doc(firestore, 'users', userId, 'conversations', conversationId);
  const messageRef = doc(collection(convoRef, 'messages'), messageData.id);
  
  let contentForUiToEncrypt;
  if (messageData.contentForUI && Array.isArray(messageData.contentForUI)) {
    contentForUiToEncrypt = JSON.stringify(messageData.contentForUI);
  } else {
    const blockId = generateId('text_block');
    contentForUiToEncrypt = JSON.stringify([
        { type: 'BLOCK_CREATE', payload: { id: blockId, type: 'text' } }, 
        { type: 'BLOCK_DELTA', payload: { id: blockId, contentChunk: String(messageData.rawContent || '') } }
    ]);
  }

  const [encryptedRawContent, encryptedContentForUI] = await Promise.all([
    CryptoUtils.encrypt(String(messageData.rawContent || ''), userId),
    CryptoUtils.encrypt(contentForUiToEncrypt, userId)
  ]);
  
  const finalMessageData = {
    ...messageData,
    rawContent: encryptedRawContent,
    contentForUI: encryptedContentForUI,
    persisted: true
  };
  delete finalMessageData.content;
  delete finalMessageData.streamEvents;
  delete finalMessageData.contentBlocks;

  batch.set(messageRef, finalMessageData);
  
  if ((attachments.files && Object.keys(attachments.files).length > 0) || (attachments.images && attachments.images.length > 0)) {
    await addAttachmentsToBatch(batch, firestore, userId, conversationId, messageRef.id, attachments, messageData.timestamp);
  }
  
  if (!existingBatch) {
    batch.set(convoRef, { lastUpdated: serverTimestamp() }, { merge: true });
    await batch.commit();
  }
  
  return messageRef.id;
};

const addAttachmentsToBatch = async (batch, firestore, userId, conversationId, messageId, attachments, messageTimestamp) => {
  const convoRef = doc(firestore, 'users', userId, 'conversations', conversationId);
  const filesCollectionRef = collection(convoRef, 'files');
  const processAttachment = async (item, type, index) => {
    const fileId = item.id || generateId(`${type}_${messageId}_`); 
    const fileRef = doc(filesCollectionRef, fileId);
    
    const urlToEncrypt = item.storageUrl || item.url;

    const fileData = {
      messageId,
      type: item.type || item.mimeType || (type === 'image' ? 'image/png' : 'application/octet-stream'),
      name: await CryptoUtils.encrypt(String(item.name || `${type}_${index}`), userId),
      size: item.size || 0,
      originalId: item.id,
      timestamp: messageTimestamp || serverTimestamp(),
    };
    if (urlToEncrypt) fileData.storagePath = await CryptoUtils.encrypt(urlToEncrypt, userId);
    else if (item.content) fileData.content = await CryptoUtils.encrypt(String(item.content), userId);
    batch.set(fileRef, fileData);
  };
  const promises = [];
  const fileArray = attachments.files ? Object.values(attachments.files) : [];
  if (fileArray.length > 0) fileArray.forEach((file, idx) => promises.push(processAttachment(file, 'file', idx)));
  if (attachments.images && attachments.images.length > 0) {
    const fileOffset = fileArray.length;
    attachments.images.forEach((img, idx) => promises.push(processAttachment(img, 'image', fileOffset + idx)));
  }
  await Promise.all(promises);
};

export const updateMessageInFirebase = async (firestore, userId, conversationId, messageId, updates) => {
    const messageRef = doc(firestore, 'users', userId, 'conversations', conversationId, 'messages', messageId);
    
    const payloadForFirebase = {};

    if ('streamEvents' in updates) {
        payloadForFirebase.contentForUI = await CryptoUtils.encrypt(JSON.stringify(updates.streamEvents), userId);
    }
    if ('rawContent' in updates) {
        payloadForFirebase.rawContent = await CryptoUtils.encrypt(updates.rawContent, userId);
    }
    if ('metadata' in updates) {
        payloadForFirebase.metadata = updates.metadata;
    }

    if (Object.keys(payloadForFirebase).length === 0) {
        return;
    }

    await updateDoc(messageRef, payloadForFirebase);
};

export const deleteMessagesFromFirebase = async (firestore, userId, conversationId, messageIds) => {
  if (!Array.isArray(messageIds) || messageIds.length === 0) return true;
  try {
    const batch = createWriteBatch(firestore);
    const convoRef = doc(firestore, 'users', userId, 'conversations', conversationId);
    for (const messageId of messageIds) {
      batch.delete(doc(collection(convoRef, 'messages'), messageId));
      const filesQuery = query(collection(convoRef, 'files'), where('messageId', '==', messageId));
      const filesSnapshot = await getDocs(filesQuery);
      filesSnapshot.docs.forEach(fileDoc => batch.delete(fileDoc.ref));
    }
    batch.update(convoRef, { lastUpdated: serverTimestamp() });
    await batch.commit();
    return true;
  } catch (error) { throw error; }
};

export const deleteConversationFromFirebase = async (firestore, userId, conversationId) => {
  try {
    const convoRef = doc(firestore, 'users', userId, 'conversations', conversationId);
    const messagesRef = collection(convoRef, 'messages');
    const filesRef = collection(convoRef, 'files');
    const [messagesSnapshot, filesSnapshot] = await Promise.all([getDocs(messagesRef), getDocs(filesRef)]);
    const batch = createWriteBatch(firestore);
    messagesSnapshot.docs.forEach(doc => batch.delete(doc.ref));
    filesSnapshot.docs.forEach(doc => batch.delete(doc.ref));
    batch.delete(convoRef);
    await batch.commit();
    return true;
  } catch (error) { throw error; }
};

export const formatConversationForStorage = (conversation) => {
  const persistFields = [
    'id', 'userId', 'botId', 'botName', 'modelId', 'modelName',
    'botPlatform', 'botModel', 'botCleanModelName', 'temperature',
    'customSystemMessage', 'name', 'status', 'memorySize', 
    'workspaceContextId', 'toolSettings', 'mcpSettings', 'messageCount', 'lastMessagePreview',
    'hasMoreMessages', 'oldestLoadedMessageTimestamp',
    'persisted', 'created', 'parManifest', 'lastImageUrl',
    'privacyMode', 'incognitoMeta'
  ];
  const dataToPersist = persistFields.reduce((acc, key) => {
    if (conversation[key] !== undefined) acc[key] = conversation[key];
    return acc;
  }, {});

  if (dataToPersist.created && !(dataToPersist.created.toDate || dataToPersist.created instanceof Date)) {
  } else if (!dataToPersist.created && !conversation.persisted) {
    dataToPersist.created = serverTimestamp();
  }

  dataToPersist.lastUpdated = serverTimestamp();
  return dataToPersist;
};

export const formatMessageForStorage = async (message, userId) => {
  let contentForUiToEncrypt;
  if (message.contentForUI && Array.isArray(message.contentForUI)) {
    contentForUiToEncrypt = JSON.stringify(message.contentForUI);
  } else {
    const blockId = generateId('text_block');
    contentForUiToEncrypt = JSON.stringify([
        { type: 'BLOCK_CREATE', payload: { id: blockId, type: 'text' } },
        { type: 'BLOCK_DELTA', payload: { id: blockId, contentChunk: String(message.rawContent || '') } }
    ]);
  }

  const [encryptedRawContent, encryptedContentForUI] = await Promise.all([
    CryptoUtils.encrypt(String(message.rawContent || ''), userId),
    CryptoUtils.encrypt(contentForUiToEncrypt, userId)
  ]);

  const baseMessage = {
    id: message.id, role: message.role,
    rawContent: encryptedRawContent,
    contentForUI: encryptedContentForUI,
    timestamp: message.timestamp || serverTimestamp(),
    status: message.status || 'complete',
    botId: message.botId || null, modelId: message.modelId || null,
    botName: message.botName || null, modelName: message.modelName || null,
  };
  if (message.retrievedMemoriesContext) {
    baseMessage.retrievedMemoriesContext = await CryptoUtils.encrypt(String(message.retrievedMemoriesContext), userId);
  }
  return baseMessage;
};

export const createConversationTitle = (content) => {
  if (typeof content !== 'string' || !content.trim()) return 'New Conversation';
  const cleanContent = content.replace(/```[\s\S]*?```/g, '').replace(/http[s]?:\/\/\S+/g, '').replace(/[^\w\s.,!?-]/g, '').trim();
  const firstSentence = cleanContent.split(/[.!?]/).filter(s => s.trim().length > 0)[0];
  if (!firstSentence) return 'New Conversation';
  return firstSentence.length > 40 ? `${firstSentence.substring(0, 37)}...` : firstSentence;
};

export const areMessagesEqual = (msg1, msg2) => {
  if (!msg1 || !msg2) return msg1 === msg2;
  return msg1.id === msg2.id && msg1.status === msg2.status && msg1.rawContent === msg2.rawContent;
};